/**
 * Address history multi input (list of entries + years-required progress) —
 * Alpine component. Moved from the inline script in
 * components/address-history-multi-input.blade.php: inline blade scripts die
 * when the component first appears via a Livewire morph (morphdom doesn't
 * execute injected script tags), which left the whole field dead — no
 * autocomplete AND no way to save manual input.
 *
 * Manual entry binds unconditionally in init() (and again when the entry form
 * opens); Google Places is a progressive enhancement layered on top when the
 * central loader resolves. Every keystroke persists the raw string into
 * formData.street so a typed address survives saveEntry() even if the user
 * never presses Enter or selects a suggestion — with or without Google.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';
import { unitField } from './unit-field';

// These strings were rendered inline by Blade ({{ __('formFilling.…') }})
// when the factory lived in the blade file. A static bundle can't call __(),
// so the blade renders them into a hidden data-island div (x-ref="i18n",
// NOT an inline <script> — Livewire v2 evals inline content on morph) and we
// JSON.parse its textContent, with English fallbacks for safety. Same pattern
// as employment-history-input.
const I18N_FALLBACK = {
    street_required: 'Street address is required',
    city_required: 'City is required',
    from_date_required: 'From date is required',
    to_date_required: 'To date is required (or mark as current)',
    date_order_error: 'To date must be after From date',
    present: 'Present',
};

function t(key) {
    // Re-read per call: cheap, and stays correct across Turbo visits that
    // switch locale without a full page load.
    let translations = {};
    try {
        const island = document.querySelector('[data-address-history-i18n]');
        if (island) translations = JSON.parse(island.textContent);
    } catch (e) { /* fall through to English */ }
    return translations[key] || I18N_FALLBACK[key] || key;
}

export default function addressHistoryMultiInput(modelValue, yearsRequired, previousAddresses = []) {
    return {
        entries: [],
        showForm: false,
        showParsedFields: false,
        editingIndex: null,
        formError: '',
        modelValue: modelValue,
        yearsRequired: yearsRequired,
        totalYears: 0,
        gaps: [],
        suggestions: previousAddresses || [],
        desktopAutocomplete: null,
        mobileAutocomplete: null,

        formData: {
            street: '',
            street2: '',
            apt: '',
            city: '',
            state: '',
            zip: '',
            country: 'United States',
            fromDate: '',
            toDate: '',
            isCurrent: false
        },

        get meetsRequirement() {
            if (this.yearsRequired <= 0) return true;
            return this.totalYears >= this.yearsRequired;
        },

        // Apt/Ste/Flr asked as a choice plus a number, stored back into the
        // same key as one canonical string -- the payload shape is unchanged.
        ...unitField('formData.apt', null),

        init() {
            this.bindManualEntry();

            loadGoogleMaps()
                .then(() => this.initAutocomplete())
                .catch(() => {}); // silent fallback — manual entry already works

            this.parseExistingValue();
            this.watchUnit();
            this.$watch('modelValue', () => this.parseExistingValue());
        },

        initAutocomplete() {
            // Desktop autocomplete
            this.$nextTick(() => {
                this.setupAutocompleteForInput(this.$refs.desktopAddressSearch, 'desktop');
                this.setupAutocompleteForInput(this.$refs.mobileAddressSearch, 'mobile');
            });
        },

        /** Always-on manual path: never depends on Google. */
        bindManualEntry() {
            this.$nextTick(() => {
                this.bindManualEntryForInput(this.$refs.desktopAddressSearch);
                this.bindManualEntryForInput(this.$refs.mobileAddressSearch);
            });
        },

        bindManualEntryForInput(input) {
            if (!input || input.dataset.manualBound === '1') return;
            input.dataset.manualBound = '1';

            // Persist raw text on every keystroke so nothing is lost if the
            // user taps Add/Save without Enter/blur/selection. The entry
            // itself only reaches the model via saveEntry() (same as the
            // Enter path — it doesn't call updateValue() either).
            input.addEventListener('input', () => {
                if (!this.showParsedFields) {
                    this.formData.street = input.value.trim();
                }
            });

            // Handle Enter key for manual entry
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = input.value.trim();
                    if (value) {
                        this.formData.street = value;
                        this.showParsedFields = true;
                    }
                }
            });

            // Delay lets a click on a Places suggestion win: place_changed sets
            // showParsedFields before this timeout inspects it.
            input.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!this.showParsedFields && input.value.trim()) {
                        this.formData.street = input.value.trim();
                        this.showParsedFields = true;
                    }
                }, 250);
            });
        },

        setupAutocompleteForInput(input, type) {
            if (!input || input.dataset.pacBound === '1') return;
            input.dataset.pacBound = '1';

            const autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ["address_components", "formatted_address"],
                types: ["address"]
            });

            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();

                if (place.address_components) {
                    this.parseAddressComponents(place);
                    this.showParsedFields = true;
                    input.blur();
                }
            });

            if (type === 'desktop') {
                this.desktopAutocomplete = autocomplete;
            } else {
                this.mobileAutocomplete = autocomplete;
            }
        },

        parseAddressComponents(place) {
            // Shared parser: country-aware city/street fallbacks (UK
            // postal_town, sublocality wards, …) — see google-place-parser.
            const p = alvvaParsePlace(place);
            this.formData.apt = '';
            this.formData.street2 = '';
            this.formData.city = p.city;
            this.formData.state = p.state;
            this.formData.country = p.country || 'United States';
            this.formData.zip = p.zip;
            this.formData.street = p.street;
        },

        resetAddressSearch() {
            this.formData.street = '';
            this.formData.apt = '';
            this.formData.street2 = '';
            this.formData.city = '';
            this.formData.state = '';
            this.formData.zip = '';
            this.formData.country = 'United States';
            this.showParsedFields = false;

            this.$nextTick(() => {
                if (this.$refs.desktopAddressSearch) {
                    this.$refs.desktopAddressSearch.value = '';
                    this.$refs.desktopAddressSearch.focus();
                }
                if (this.$refs.mobileAddressSearch) {
                    this.$refs.mobileAddressSearch.value = '';
                }
            });
        },

        useSuggestion(suggestion) {
            this.formData.street = suggestion.street || '';
            this.formData.street2 = suggestion.street2 || '';
            this.formData.apt = suggestion.apt || suggestion.address2 || '';
            this.formData.city = suggestion.city || '';
            this.formData.state = suggestion.state || '';
            this.formData.zip = suggestion.zip || '';
            this.formData.country = suggestion.country || 'United States';
            this.showParsedFields = true;
        },

        parseExistingValue() {
            // Don't overwrite a field the user is actively editing — the
            // modelValue watcher fires on our own keystrokes too, which clobbered
            // fast typing (chars dropped / value flashed).
            if (this.$el && this.$el.contains(document.activeElement)) return;
            const value = this.modelValue || '';
            if (!value) {
                this.entries = [];
                this.calculateProgress();
                return;
            }

            try {
                const data = JSON.parse(value);
                this.entries = Array.isArray(data.entries) ? data.entries : [];
                this.calculateProgress();
            } catch (e) {
                this.entries = [];
                this.calculateProgress();
            }
        },

        resetFormData() {
            this.formData = {
                street: '',
                street2: '',
                apt: '',
                city: '',
                state: '',
                zip: '',
                country: 'United States',
                fromDate: '',
                toDate: '',
                isCurrent: false
            };
            this.formError = '';
            this.showParsedFields = false;

            // Clear search inputs
            if (this.$refs.desktopAddressSearch) {
                this.$refs.desktopAddressSearch.value = '';
            }
            if (this.$refs.mobileAddressSearch) {
                this.$refs.mobileAddressSearch.value = '';
            }
        },

        isUS() {
            return !this.formData.country || this.formData.country === 'United States' || this.formData.country === 'US' || this.formData.country === 'USA';
        },

        handleCountryChange() {
            const usStates = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR','VI','GU','AS','MP'];
            if (!this.isUS() && usStates.includes(this.formData.state)) {
                this.formData.state = '';
            }
        },

        formatPostalCode() {
            if (this.isUS()) {
                this.formData.zip = this.formData.zip.replace(/[^\d-]/g, '').substring(0, 10);
            } else {
                this.formData.zip = this.formData.zip.replace(/[^\w\s-]/g, '').substring(0, 10);
            }
        },

        openForm() {
            this.resetFormData();
            this.editingIndex = null;
            this.showForm = true;

            // Re-initialize autocomplete after form opens
            this.$nextTick(() => {
                this.bindManualEntry();
                loadGoogleMaps()
                    .then(() => this.initAutocomplete())
                    .catch(() => {}); // silent fallback — manual entry already works
            });
        },

        cancelForm() {
            this.showForm = false;
            this.editingIndex = null;
            this.resetFormData();
        },

        validateForm() {
            if (!this.formData.street.trim()) {
                this.formError = t('street_required');
                return false;
            }
            if (!this.formData.city.trim()) {
                this.formError = t('city_required');
                return false;
            }
            if (!this.formData.fromDate) {
                this.formError = t('from_date_required');
                return false;
            }
            if (!this.formData.isCurrent && !this.formData.toDate) {
                this.formError = t('to_date_required');
                return false;
            }
            if (!this.formData.isCurrent && this.formData.toDate < this.formData.fromDate) {
                this.formError = t('date_order_error');
                return false;
            }
            this.formError = '';
            return true;
        },

        saveEntry() {
            if (!this.validateForm()) return;

            const entry = { ...this.formData };

            if (this.editingIndex !== null) {
                this.entries[this.editingIndex] = entry;
            } else {
                this.entries.push(entry);
            }

            // Sort entries by date (most recent first)
            this.entries.sort((a, b) => {
                const dateA = a.isCurrent ? new Date() : new Date(a.toDate);
                const dateB = b.isCurrent ? new Date() : new Date(b.toDate);
                return dateB - dateA;
            });

            this.calculateProgress();
            this.updateValue();
            this.cancelForm();
        },

        editEntry(index) {
            this.editingIndex = index;
            this.formData = { ...this.entries[index] };
            this.showParsedFields = true; // Show parsed fields when editing
            this.showForm = true;
        },

        removeEntry(index) {
            this.entries.splice(index, 1);
            this.calculateProgress();
            this.updateValue();
        },

        // Overlapping periods are allowed (living at two places at once
        // happens) — merge entries into a union of intervals so overlaps
        // count calendar time once and gaps reflect the real timeline.
        mergedIntervals() {
            const now = new Date();
            const ranges = this.entries
                .map(e => ({
                    from: new Date(e.fromDate),
                    to: e.isCurrent ? now : new Date(e.toDate),
                }))
                .filter(r => !isNaN(r.from) && !isNaN(r.to) && r.to >= r.from)
                .sort((a, b) => a.from - b.from);

            const merged = [];
            for (const r of ranges) {
                const last = merged[merged.length - 1];
                if (last && r.from <= last.to) {
                    if (r.to > last.to) last.to = r.to;
                } else {
                    merged.push({ from: r.from, to: r.to });
                }
            }
            return merged;
        },

        calculateProgress() {
            let totalMonths = 0;
            this.gaps = [];

            if (this.entries.length === 0) {
                this.totalYears = 0;
                return;
            }

            const merged = this.mergedIntervals();

            merged.forEach((interval, i) => {
                const months = (interval.to.getFullYear() - interval.from.getFullYear()) * 12
                             + (interval.to.getMonth() - interval.from.getMonth());
                totalMonths += Math.max(0, months);

                // Check for gap with the previous merged block
                if (i > 0) {
                    const prevTo = merged[i - 1].to;
                    const gapMonths = (interval.from.getFullYear() - prevTo.getFullYear()) * 12
                                    + (interval.from.getMonth() - prevTo.getMonth());

                    if (gapMonths > 1) {
                        this.gaps.push({
                            from: prevTo,
                            to: interval.from,
                            months: gapMonths
                        });
                    }
                }
            });

            this.totalYears = totalMonths / 12;
        },

        formatDateRange(entry) {
            const from = this.formatFullDate(entry.fromDate);
            const to = entry.isCurrent ? t('present') : this.formatFullDate(entry.toDate);
            return from + ' - ' + to;
        },

        formatFullDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        },

        updateValue() {
            const data = {
                entries: this.entries,
                totalYears: this.totalYears,
                meetsRequirement: this.meetsRequirement,
                hasGaps: this.gaps.length > 0
            };

            const value = JSON.stringify(data);
            this.modelValue = value;
            if (this.$refs.hiddenInput) {
                this.$refs.hiddenInput.value = value;
                this.$refs.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }
}
